-- | This module implements the root form state which contains all of the
-- | widgets within a form and their state.
module Widgets.Form where

import Common

import API.FormBase (FormBasic, FormID)
import API.FormType (FormType)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut (Json)
import Data.Array (catMaybes, concatMap)
import Error (ErrorContext(..), tagAction, tagContext)
import GraphQL.Shape (QueryShape)
import GraphQL.Type (innerId)
import Widgets.Form.Access (FormAccess, fullFormAccess)
import Widgets.Form.Class (FormContext, FormWidgetResult, asField, blankFormContext, fieldPaths)
import Widgets.Form.WidgetMap (WidgetMap, getWMData, renderWidgets, setWMData)


-- | Defines the layout and current state of a form as a list of form widgets.
type FormState =
    { id :: FormID
    , label :: String
    , access :: FormAccess
    , fields :: WidgetMap
    }

-- | Renders all of a form's widgets based on the given `FormState`.
-- |
-- | On user input, the `FormState` will be updated with new values and the new
-- | state returned. The new state will be returned wrapped in a
-- | `FormWidgetResult` containing any additional actions that should be
-- | executed on the entire form.
-- |
-- | Caller should call this function again with the new state to continue
-- | rendering.
formWidget :: FormContext -> FormState -> Widget HTML (FormWidgetResult FormState)
formWidget fc state = (state { fields = _ } <$> _) <$> D.div
    [ P._id $ innerId state.id, P.key $ innerId state.id ]
    [ D.div [ P.className "step-3-form" ] $ renderWidgets fc state.fields ]

-- | Encodes values of all widgets in the form into a JSON object to be sent to
-- | the backend.
formData :: FormState -> Json
formData state = getWMData blankFormContext state.fields

-- | Returns a `QueryShape` object describing the shape of this form's JSON
-- | data.
formShape :: FormState -> QueryShape
formShape state = catMaybes $ map asField state.fields

formFieldPaths :: FormState -> Array String
formFieldPaths state = concatMap (fieldPaths blankFormContext) state.fields

-- | Attempts to load the given JSON data into the form's field values.
setFormData :: FormContext -> Json -> FormState -> Either ErrorWrapper FormState
setFormData fc json state = tagAction "loading data into form" $ tagContext jsonCtx do
    fields <- setWMData fc state.fields json
    pure $ state { fields = fields }
  where
    jsonCtx = LoadingJson "form data" json

fromFormType :: FormType -> FormState
fromFormType { id, label, fields } = { id, label, access: fullFormAccess, fields }

-- | Converts a basic form into a form state with no fields. Mainly for testing.
fromFormBasic :: FormBasic -> FormState
fromFormBasic { id, label } = { id, label, access: fullFormAccess, fields: [] }
